import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.97, y: 12 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    y: 12,
    transition: { duration: 0.2, ease: 'easeIn' },
  },
};

const PlanLimitReachedModal = ({
  open,
  onClose,
  onUpgrade,
  activeOrder,
  limitType,
}) => {
  const isActive = activeOrder?.status === 'active';
  const price = Number(activeOrder?.package_price ?? 0);
  const planName = (activeOrder?.package_name || '').toLowerCase();

  const isFreePlan = planName === 'free' || price <= 0;
  const isPaidPlan = isActive && !isFreePlan;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl px-12 py-10"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-indigo-50">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-indigo-500"
                >
                  <path
                    d="M12 3v4M12 17v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M3 12h4M17 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>

            <h2 className="text-center text-lg font-semibold text-gray-900 mb-2">
              Plan Limit Reached
            </h2>

            <p className="text-center text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
              {isPaidPlan
                ? `You’ve reached the usage limit for your current plan.`
                : `You’ve used all your available AI processing limits for your current plan.
                   Upgrade to continue accessing our tools.`}
            </p>

            {/* CTA ONLY for free / not-paid */}
            {!isPaidPlan && (
              <div className="mt-6 flex flex-col items-center gap-3">
                <button
                  onClick={onUpgrade}
                  className="px-6 py-2.5 rounded-md bg-indigo-600
                    text-white text-sm font-medium hover:bg-indigo-700 transition"
                >
                  See All Plans
                </button>
              </div>
            )}

            <div className="mt-10 pt-4 border-t flex justify-center gap-6 text-[11px] text-gray-400">
              <a
                href="https://www.ulegal.ai/terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-300"
              >
                Terms of Service
              </a>
              <a
                href="https://www.ulegal.ai/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-300"
              >
                Privacy Policy
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlanLimitReachedModal;
